import React from 'react';
import { usePolling } from '../hooks/useApi';
import { api } from '../utils/api';
import { formatHistoryTime } from '../utils/time';
import {
  formatCexSummary,
  formatDexConfirmTime,
  formatDexSubmitTime,
  formatDexSummary,
  formatDexTotalTime,
  formatDiagnosticsSummary,
  formatRecoverySummary,
  formatTotalsSummary,
  parseTradeDetails,
} from '../utils/tradeDetails';
import { formatSignedUsd } from '../utils/numberFormat';

const AUDIT_PAGE_SIZE = 200;

export function AuditPage() {
  const [limit, setLimit] = React.useState(AUDIT_PAGE_SIZE);
  const { data: auditData, error, loading, failCount } = usePolling(
    () => api.getAuditTrades({ limit, offset: 0 }),
    15000,
    [limit]
  );
  const trades = auditData?.trades || [];
  const totalTrades = auditData?.total ?? trades.length;
  const hasMore = auditData?.hasMore || trades.length < totalTrades;

  function formatUsd(val: number | null): string {
    return formatSignedUsd(val);
  }

  const statusLabels: Record<string, string> = {
    success: 'успешно', partial: 'частично', failed: 'неуспешно', warning: 'внимание',
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-1">
        <div className="section-title" style={{ margin: 0 }}>Аудит: все сделки</div>
        <div className="text-xs text-muted">
          загружено {trades.length} / {totalTrades}
          {loading && trades.length === 0 ? ' · загрузка...' : ''}
          {failCount > 0 && error ? ' · ' + error : ''}
        </div>
      </div>
      <div style={{ overflowX: 'auto' }}>
        <table className="table">
          <thead>
            <tr>
              <th>ID</th><th>Токен</th><th>Маршрут</th><th>Размер</th>
              <th>Ожидали</th><th>Итог</th><th>Статус</th>
              <th>Дельты</th><th>Диагн.</th><th>DEX нога</th><th>Возврат</th><th>CEX нога</th><th>DEX send</th><th>DEX confirm</th><th>DEX total</th><th>CEX время</th>
              <th>Причина</th><th>Когда</th>
            </tr>
          </thead>
          <tbody>
            {trades?.map((t: any) => {
              const details = parseTradeDetails(t);
              return (
                <tr key={t.id}>
                  <td className="text-muted text-xs">{t.id}</td>
                  <td className="route-token">{t.token}</td>
                  <td className="text-xs">DEX {t.direction === 'dex_to_cex' ? '->' : '<-'} {t.cex}</td>
                  <td className="text-mono">${t.amountUsd}</td>
                  <td className="text-mono">{formatUsd(t.expectedProfitUsd)}</td>
                  <td className={'text-mono ' + (t.actualProfitUsd !== null && t.actualProfitUsd >= 0 ? 'spread positive' : 'spread negative')}>
                    {formatUsd(t.actualProfitUsd)}
                  </td>
                  <td>
                    <span className={'badge ' + (
                      details?.recovery?.status === 'completed' ? 'badge-warning' :
                      t.status === 'success' ? 'badge-success' :
                      t.status === 'partial' ? 'badge-warning' :
                      t.status === 'failed' ? 'badge-danger' : 'badge-neutral'
                    )}>{details?.recovery?.status === 'completed' ? 'возвращено' : (statusLabels[t.status] || t.status)}</span>
                  </td>
                  <td className="text-xs text-mono" style={{ minWidth: 220 }}>{details ? formatTotalsSummary(details) : '-'}</td>
                  <td className="text-xs text-mono" style={{ minWidth: 260 }}>{formatDiagnosticsSummary(details) || '-'}</td>
                  <td className="text-xs">{details ? formatDexSummary(details) : (t.dexLeg || '-')}</td>
                  <td className="text-xs" style={{ minWidth: 260 }}>{formatRecoverySummary(details) || '-'}</td>
                  <td className="text-xs">{details ? formatCexSummary(details) : (t.cexLeg || '-')}</td>
                  <td className="text-xs">{formatDexSubmitTime(details)}</td>
                  <td className="text-xs">{formatDexConfirmTime(details)}</td>
                  <td className="text-xs">{formatDexTotalTime(details, t.dexTimeMs)}</td>
                  <td className="text-xs">{t.cexTimeMs !== null ? (t.cexTimeMs / 1000).toFixed(1) + 'с' : '-'}</td>
                  <td className="text-xs" style={{ color: 'var(--text-muted)', maxWidth: 120, overflow: 'hidden', textOverflow: 'ellipsis' }}>{t.reason || '-'}</td>
                  <td className="text-xs text-muted" title={t.createdAt}>{formatHistoryTime(t.createdAt)}</td>
                </tr>
              );
            })}
            {trades.length === 0 && !loading && (
              <tr><td colSpan={18} className="text-secondary text-xs">Сделок пока нет</td></tr>
            )}
          </tbody>
        </table>
      </div>
      {hasMore && (
        <div className="flex justify-center mt-2">
          <button className="btn btn-sm btn-outline" onClick={() => setLimit(value => value + AUDIT_PAGE_SIZE)}>
            Показать ещё {AUDIT_PAGE_SIZE}
          </button>
        </div>
      )}
    </div>
  );
}
